import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { Property } from 'src/app/model/property';
import { ActivatedRoute } from '@angular/router';
import { PropertyService } from 'src/app/services/property.service';

@Component({
  selector: 'app-view-details',
  templateUrl: './view-details.component.html',
  styleUrls: ['./view-details.component.css']
})
export class ViewDetailsComponent implements OnInit {

  // =========================================================
  // PROPERTIES & ROUTING DATA
  // =========================================================
  property: Property | null = null;
  allProperties: Property[] = [];
  similar: Property[] = [];
  type: string = '';
  id: number = 0;

  // =========================================================
  // IMAGE GALLERY
  // =========================================================
  images: string[] = [
    'assets/images/buy-hero.webp',
    'assets/images/commercial-hero.webp',
    'assets/images/kl.webp',
    'assets/images/kl-rent-bg.webp',
    'assets/images/commercial-hero.webp',
    'assets/images/buy-hero.webp',
    'assets/images/kl-rent-bg.webp'
  ];
  selectedImage: string = this.images[0];
  currentIndex = 0;
  touchStartX = 0;
  touchEndX = 0;
  showLightbox = false;

  // =========================================================
  // PROPERTY CONTENT
  // =========================================================
  showAll = false;
  showAllCommonFacilities = false;
  amenities: string[] = [];
  commonFacilities: string[] = [];
  mapUrl = '';
  videoURL = '';
  showEnquiry = false;

  // =========================================================
  // SIMILAR PROPERTY PAGINATION
  // =========================================================
  displayedSimilarProperties: Property[] = [];
  currentSimilarPage: number = 1;
  similarPageSize: number = 8;
  similarPageStartIndex: number = 0;
  similarPageEndIndex: number = 0;
  totalSimilarPages: number[] = [];
  visibleSimilarPages: number[] = [];

  constructor(
    private location: Location,
    private route: ActivatedRoute,
    private propertyService: PropertyService
  ) {}

  // =========================================================
  // INIT
  // =========================================================
  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.type = params.get('type') || 'rent';
      this.id = Number(params.get('id')) || 0;

      const cachedList = this.propertyService.getCachedProperties(this.type);

      if (cachedList) {
        this.preparePropertyData(cachedList);
      } else {
        this.propertyService
          .getPropertiesByType(this.type, 'vyenpropertyadvisor')
          .subscribe({
            next: (data) => this.preparePropertyData(data),
            error: (err) => console.error('Error fetching properties:', err)
          });
      }
    });
  }

  // =========================================================
  // PREPARE PROPERTY DATA
  // =========================================================
  preparePropertyData(list: Property[]): void {
    this.allProperties = list;
    this.property = list.find(p => p.id === this.id) || null;

    if (!this.property) {
      return;
    }

    this.amenities = this.property.amenities || [];
    this.commonFacilities = this.property.commonFacilities || [];
    this.mapUrl = this.property.location || '';
    this.videoURL = this.property.videoURL || '';
    
    this.similar = list.filter(p => p.id !== this.property!.id);

    this.images = (this.property.imageUrls && this.property.imageUrls.length > 0)
      ? this.property.imageUrls
      : ['assets/images/buy-hero.webp'];

    this.currentIndex = 0;
    this.selectedImage = this.images[0];

    this.setupSimilarPagination();
  }

  // =========================================================
  // SIMILAR PROPERTY PAGINATION
  // =========================================================
  setupSimilarPagination(): void {
    if (!this.similar || this.similar.length === 0) {
      this.displayedSimilarProperties = [];
      this.totalSimilarPages = [];
      this.visibleSimilarPages = [];
      this.similarPageStartIndex = 0;
      this.similarPageEndIndex = 0;
      return;
    }

    const totalPageCount = Math.ceil(this.similar.length / this.similarPageSize);
    this.totalSimilarPages = [];

    for (let i = 1; i <= totalPageCount; i++) {
      this.totalSimilarPages.push(i);
    }

    this.currentSimilarPage = 1;
    this.updateVisibleSimilarPages();
    this.updateDisplayedSimilarProperties();
  }

  updateDisplayedSimilarProperties(): void {
    const startIndex = (this.currentSimilarPage - 1) * this.similarPageSize;
    const endIndex = startIndex + this.similarPageSize;

    this.displayedSimilarProperties = this.similar.slice(startIndex, endIndex);
    this.similarPageStartIndex = startIndex;
    this.similarPageEndIndex = Math.min(endIndex, this.similar.length);
  }

  updateVisibleSimilarPages(): void {
    const total = this.totalSimilarPages.length;
    const current = this.currentSimilarPage;
    this.visibleSimilarPages = [];

    if (total <= 7) {
      for (let i = 1; i <= total; i++) {
        this.visibleSimilarPages.push(i);
      }
      return;
    }

    if (current <= 4) {
      this.visibleSimilarPages = [1, 2, 3, 4, 5, -1, total];
      return;
    }

    if (current >= total - 3) {
      this.visibleSimilarPages = [1, -1, total - 4, total - 3, total - 2, total - 1, total];
      return;
    }

    this.visibleSimilarPages = [1, -1, current - 2, current - 1, current, current + 1, current + 2, -1, total];
  }

  goToSimilarPage(page: number): void {
    if (page < 1 || page > this.totalSimilarPages.length) {
      return;
    }

    this.currentSimilarPage = page;
    this.updateDisplayedSimilarProperties();
    this.updateVisibleSimilarPages();
    this.scrollToSimilarProperties();
  }

  previousSimilarPage(): void {
    if (this.currentSimilarPage > 1) {
      this.currentSimilarPage--;
      this.updateDisplayedSimilarProperties();
      this.updateVisibleSimilarPages();
      this.scrollToSimilarProperties();
    }
  }

  nextSimilarPage(): void {
    if (this.currentSimilarPage < this.totalSimilarPages.length) {
      this.currentSimilarPage++;
      this.updateDisplayedSimilarProperties();
      this.updateVisibleSimilarPages();
      this.scrollToSimilarProperties();
    }
  }

  private scrollToSimilarProperties(): void {
    setTimeout(() => {
      const element = document.querySelector('.similar-properties-section');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
  }

  // =========================================================
  // IMAGE GALLERY & LIGHTBOX
  // =========================================================
  openLightbox(): void {
    this.showLightbox = true;
  }

  closeLightbox(): void {
    this.showLightbox = false;
  }

  nextImage(): void {
    if (!this.images.length) return;
    this.currentIndex = (this.currentIndex + 1) % this.images.length;
    this.selectedImage = this.images[this.currentIndex];
  }

  prevImage(): void {
    if (!this.images.length) return;
    this.currentIndex = (this.currentIndex - 1 + this.images.length) % this.images.length;
    this.selectedImage = this.images[this.currentIndex];
  }

  selectImage(img: string): void {
    this.selectedImage = img;
    this.currentIndex = this.images.indexOf(img);
  }

  // =========================================================
  // MOBILE SWIPE
  // =========================================================
  onTouchStart(event: TouchEvent): void {
    this.touchStartX = event.changedTouches[0].screenX;
  }

  onTouchEnd(event: TouchEvent): void {
    this.touchEndX = event.changedTouches[0].screenX;
    this.handleSwipe();
  }

  handleSwipe(): void {
    const swipeDistance = this.touchEndX - this.touchStartX;
    if (swipeDistance > 50) {
      this.prevImage();
    } else if (swipeDistance < -50) {
      this.nextImage();
    }
  }

  // =========================================================
  // ACTIONS & ENQUIRY
  // =========================================================
  toggleEnquiry(): void {
    this.showEnquiry = !this.showEnquiry;
  }

  openWhatsApp(): void {
    window.open('https://wa.me/+60169382650', '_blank');
  }

  goBack(): void {
    this.location.back();
  }

  shareProperty(event: Event): void {
    event.preventDefault();

    const title = (this.property && this.property.name) ? this.property.name : 'Property Listing';
    const shareData = {
      title: title,
      text: 'Check out this amazing property!',
      url: window.location.href
    };

    if ((navigator as any).share) {
      (navigator as any).share(shareData).catch((error: any) =>
        console.error('Share failed:', error)
      );
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Sharing not supported on this browser. Link copied!');
    }
  }

  // =========================================================
  // AMENITIES & FACILITIES GETTERS / TOGGLES
  // =========================================================
  get displayedAmenities(): string[] {
    return this.showAll ? this.amenities : this.amenities.slice(0, 6);
  }

  toggleAmenities(): void {
    this.showAll = !this.showAll;
  }

  get displayedCommonFacilities(): string[] {
    return this.showAllCommonFacilities ? this.commonFacilities : this.commonFacilities.slice(0, 6);
  }

  toggleCommonFacilities(): void {
    this.showAllCommonFacilities = !this.showAllCommonFacilities;
  }

}